import { useEffect, useMemo, useState } from 'react';
import { Loader2, Zap } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/command';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/tooltip';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { useQuickMessages } from '@Hooks/useQuickMessages';
import type { QuickMessage } from '@Types/messageTemplate';

const SEARCH_DEBOUNCE_MS = 150;
const UNCATEGORIZED_LABEL = 'Uncategorized';
const MERGE_TAG_PATTERN = /\[\[|\{\{/;

type InboxChannel = 'SMS' | 'EMAIL' | 'APP';

interface QuickMessagePickerProps {
  channel: InboxChannel;
  /**
   * Whether the recipient is a known fish. Merge tags resolve against the
   * recipient fish at send time; without one they would go out as raw tags,
   * so quick messages containing tags are disabled.
   */
  canResolveMergeTags: boolean;
  onSelect: (message: QuickMessage) => void;
  disabled?: boolean;
}

/** Rows arrive ordered by category, so consecutive rows form each group. */
const groupByCategory = (messages: QuickMessage[]) => {
  const groups: { category: string; messages: QuickMessage[] }[] = [];
  for (const message of messages) {
    const category = message.category ?? UNCATEGORIZED_LABEL;
    const last = groups[groups.length - 1];
    if (last?.category === category) {
      last.messages.push(message);
    } else {
      groups.push({ category, messages: [message] });
    }
  }
  return groups;
};

export function QuickMessagePicker({
  channel,
  canResolveMergeTags,
  onSelect,
  disabled,
}: QuickMessagePickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedSearch(search.trim()),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
  }, [search]);

  const { data: messages = [], isFetching } = useQuickMessages({
    // In-app messages are plain text like SMS, so they share SMS quick messages.
    method: channel === 'EMAIL' ? 'EMAIL' : 'SMS',
    q: debouncedSearch,
    enabled: open,
  });

  const groups = useMemo(() => groupByCategory(messages), [messages]);

  const blockedIds = useMemo(
    () =>
      new Set(
        canResolveMergeTags
          ? []
          : messages
              .filter(
                (message) =>
                  MERGE_TAG_PATTERN.test(message.messageBody) ||
                  MERGE_TAG_PATTERN.test(message.messageSubject ?? ''),
              )
              .map((message) => message.id),
      ),
    [messages, canResolveMergeTags],
  );

  // cmdk keeps its highlighted value across result changes; once the server
  // returns a new list that value is gone and Enter selects nothing, so the
  // highlight follows the first selectable result.
  const [activeId, setActiveId] = useState('');
  useEffect(() => {
    setActiveId(messages.find((message) => !blockedIds.has(message.id))?.id ?? '');
  }, [messages, blockedIds]);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setSearch('');
      setDebouncedSearch('');
    }
  };

  const handleSelect = (message: QuickMessage) => {
    onSelect(message);
    handleOpenChange(false);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label="Add Quick Message"
              disabled={disabled}
              className="mr-1 h-8 w-8 shrink-0 text-gray-500"
            >
              <Zap className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>Add Quick Message</TooltipContent>
      </Tooltip>

      <PopoverContent
        side="top"
        align="start"
        className="w-80 p-0"
        // The composer focuses its textarea after inserting; returning focus
        // to this trigger on close would take it back.
        onCloseAutoFocus={(event) => event.preventDefault()}
      >
        <Command shouldFilter={false} value={activeId} onValueChange={setActiveId}>
          <CommandInput
            placeholder="Search quick messages..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList className="max-h-72">
            <CommandEmpty>
              {isFetching ? (
                <div className="flex justify-center">
                  <Loader2 className="h-4 w-4 animate-spin text-gray-500" />
                </div>
              ) : (
                'No quick messages found.'
              )}
            </CommandEmpty>
            {groups.map((group) => (
              <CommandGroup key={group.category} heading={group.category}>
                {group.messages.map((message) => {
                  const blocked = blockedIds.has(message.id);
                  return (
                    <CommandItem
                      key={message.id}
                      value={message.id}
                      disabled={blocked}
                      onSelect={() => handleSelect(message)}
                      className="flex-col items-start gap-0.5"
                    >
                      <span className="w-full truncate font-medium">
                        {message.name}
                      </span>
                      <span className="line-clamp-2 w-full text-xs text-gray-500">
                        {message.messageBody}
                      </span>
                      {blocked && (
                        <span className="text-xs text-amber-600">
                          Has merge tags; this recipient isn't a known fish
                        </span>
                      )}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default QuickMessagePicker;
